import React, { useState, useEffect } from "react";
import { runValuation, getRentEstimate, getWithdrawn, saveEngineValuation, downloadValuationReport, fetchProperties } from "../../../api";
import { Landmark, Loader2, Home, PoundSterling, AlertCircle, MapPin, FileDown, Save, CheckCircle2 } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import PostcodeAddressPicker from "../../PostcodeAddressPicker";
import { Dialog, DialogContent, DialogTitle } from "../../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

const money = (n) => (n || n === 0 ? `£${Number(n).toLocaleString()}` : "—");

const AdminValuationEngine = () => {
  const { toast } = useToast();
  const [tab, setTab] = useState("value");
  const [form, setForm] = useState({ postcode: "BA1 2FJ", propertyType: "Terraced House", bedrooms: 3, bathrooms: 2, sqft: 1100, finish: "average", outdoorSpace: "garden", parking: 0 });
  const [valAddr, setValAddr] = useState("");
  const [val, setVal] = useState(null);
  const [rentForm, setRentForm] = useState({ postcode: "BA1", currentRent: 1750, bedrooms: 2 });
  const [rentAddr, setRentAddr] = useState("");
  const [rent, setRent] = useState(null);
  const [withdrawn, setWithdrawn] = useState([]);
  const [busy, setBusy] = useState(false);
  const [properties, setProperties] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sf, setSf] = useState({ propertyId: "", name: "", email: "" });

  useEffect(() => {
    getWithdrawn().then((d) => setWithdrawn(d.items || [])).catch(() => {});
    fetchProperties().then((d) => setProperties(d.items || d || [])).catch(() => setProperties([]));
  }, []);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try { await downloadValuationReport({ ...form, address: valAddr }); toast({ title: "PDF generated" }); }
    catch { toast({ title: "Couldn't generate PDF", variant: "destructive" }); }
    finally { setPdfBusy(false); }
  };

  const saveVal = async () => {
    setSaving(true);
    try {
      const r = await saveEngineValuation({ ...form, address: valAddr, estimate: val.estimate, range: val.range, source: val.source, propertyId: sf.propertyId || null, name: sf.name || null, email: sf.email || null });
      toast({ title: "Valuation saved", description: r.linkedProperty ? "Linked to the property and added to Valuations." : "Added to the Valuations list." });
      setSaveOpen(false); setSf({ propertyId: "", name: "", email: "" });
    } catch { toast({ title: "Couldn't save", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const doValue = async () => {
    if (!form.postcode) { toast({ title: "Select a property address first" }); return; }
    setBusy(true);
    try { setVal(await runValuation(form)); } catch { toast({ title: "Valuation failed", variant: "destructive" }); }
    setBusy(false);
  };
  const doRent = async () => {
    if (!rentForm.postcode) { toast({ title: "Select a property address first" }); return; }
    setBusy(true);
    try { setRent(await getRentEstimate(rentForm)); } catch { toast({ title: "Rent estimate failed", variant: "destructive" }); }
    setBusy(false);
  };

  const Tabs = () => (
    <div className="flex gap-1.5 bg-white border border-[#f0ece5] rounded-xl p-1.5 w-fit">
      {[["value", "Valuation"], ["rent", "Rent gap"], ["withdrawn", "Withdrawn detector"]].map(([v, l]) => (
        <button key={v} onClick={() => setTab(v)} data-testid={`val-tab-${v}`} className={`px-4 py-2 rounded-lg text-[13px] ${tab === v ? "bg-[#b8935f] text-white" : "text-[#6a6a6a] hover:bg-[#faf7f2]"}`}>{l}</button>
      ))}
    </div>
  );
  const inp = "text-[13px] border border-[#f0ece5] rounded-lg px-3 py-2 w-full";

  return (
    <div className="space-y-5" data-testid="admin-valuation-engine">
      <div>
        <h2 className="text-[22px] font-semibold text-[#2b2b2b] flex items-center gap-2"><Landmark size={20} className="text-[#b8935f]" /> Valuation & Rent Engine</h2>
        <p className="text-[13px] text-[#8a8a8a] mt-1">AVM with comparables, rent-gap detection and withdrawn-listing classification. Estimates only.</p>
      </div>
      <Tabs />

      {tab === "value" && (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white border border-[#f0ece5] rounded-xl p-4 space-y-3">
            <div>
              <PostcodeAddressPicker
                postcode={form.postcode}
                address={valAddr}
                compact
                testid="valeng-address"
                onPick={({ address, postcode }) => { if (address) setValAddr(address); if (postcode) setForm((f) => ({ ...f, postcode })); }}
              />
              {form.postcode && <p className="text-[12px] text-[#8a8a8a] mt-1.5 flex items-center gap-1"><MapPin size={12} className="text-[#b8935f]" /> Using postcode <strong className="text-[#4a4a4a]">{form.postcode}</strong></p>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select className={inp} value={form.propertyType} onChange={(e) => setForm({ ...form, propertyType: e.target.value })}>{["Terraced House", "Semi-Detached House", "Detached House", "Apartment"].map((t) => <option key={t}>{t}</option>)}</select>
              <input className={inp} type="number" placeholder="Bedrooms" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: +e.target.value })} />
              <input className={inp} type="number" placeholder="Bathrooms" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: +e.target.value })} />
              <input className={inp} type="number" placeholder="Sq ft" value={form.sqft} onChange={(e) => setForm({ ...form, sqft: +e.target.value })} />
              <select className={inp} value={form.finish} onChange={(e) => setForm({ ...form, finish: e.target.value })}>{["needs_work", "average", "high", "renovated"].map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}</select>
            </div>
            <button onClick={doValue} disabled={busy} data-testid="val-run" className="w-full bg-[#b8935f] hover:bg-[#a37f4e] text-white rounded-lg py-2.5 text-[14px] disabled:opacity-60 flex items-center justify-center gap-2">{busy ? <Loader2 size={16} className="animate-spin" /> : null} Estimate value</button>
          </div>
          <div className="bg-white border border-[#f0ece5] rounded-xl p-4" data-testid="val-result">
            {!val ? <div className="text-[13px] text-[#8a8a8a] py-8 text-center">Run an estimate to see the result.</div> : (
              <div className="space-y-3">
                <div><div className="text-[11px] text-[#8a8a8a] uppercase">Estimated value ({val.source})</div><div className="text-[32px] font-bold text-[#2b2b2b]">{money(val.estimate)}</div><div className="text-[13px] text-[#6a6a6a]">Range {money(val.range[0])} – {money(val.range[1])} · confidence {String(val.confidence)}</div></div>
                {val.adjustments?.length > 0 && <div className="flex flex-wrap gap-1.5">{val.adjustments.map((a, i) => <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full ${a.direction === "+" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{a.direction} {a.factor}</span>)}</div>}
                {val.comparables?.length > 0 && <div><div className="text-[12px] font-medium text-[#6a6a6a] mb-1">Comparables</div><div className="space-y-1">{val.comparables.slice(0, 5).map((c, i) => <div key={i} className="text-[12px] text-[#4a4a4a] flex justify-between"><span className="truncate">{c.address}</span><span className="font-medium">{money(c.price)}</span></div>)}</div></div>}
                <p className="text-[11px] text-[#a8a8a8] italic">{val.note}</p>
                <div className="flex items-center gap-2 pt-2 border-t border-[#f5f1ea]">
                  <button onClick={downloadPdf} disabled={pdfBusy} data-testid="val-pdf" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] disabled:opacity-60">{pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Download PDF</button>
                  <button onClick={() => setSaveOpen(true)} data-testid="val-save" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#b8935f] text-white text-[12px] font-500 hover:bg-[#a67f4d]"><Save size={14} /> Save valuation</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "rent" && (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white border border-[#f0ece5] rounded-xl p-4 space-y-3">
            <PostcodeAddressPicker
              postcode={rentForm.postcode}
              address={rentAddr}
              compact
              testid="valeng-rent-address"
              onPick={({ address, postcode }) => { if (address) setRentAddr(address); if (postcode) setRentForm((f) => ({ ...f, postcode })); }}
            />
            {rentForm.postcode && <p className="text-[12px] text-[#8a8a8a] flex items-center gap-1"><MapPin size={12} className="text-[#b8935f]" /> Using postcode <strong className="text-[#4a4a4a]">{rentForm.postcode}</strong></p>}
            <input className={inp} type="number" placeholder="Current rent (pcm)" value={rentForm.currentRent} onChange={(e) => setRentForm({ ...rentForm, currentRent: +e.target.value })} />
            <input className={inp} type="number" placeholder="Bedrooms" value={rentForm.bedrooms} onChange={(e) => setRentForm({ ...rentForm, bedrooms: +e.target.value })} />
            <button onClick={doRent} disabled={busy} data-testid="rent-run" className="w-full bg-[#b8935f] hover:bg-[#a37f4e] text-white rounded-lg py-2.5 text-[14px] disabled:opacity-60">Estimate market rent</button>
          </div>
          <div className="bg-white border border-[#f0ece5] rounded-xl p-4" data-testid="rent-result">
            {!rent ? <div className="text-[13px] text-[#8a8a8a] py-8 text-center">Estimate a rent to see the gap.</div> : (
              <div className="space-y-3">
                <div className="flex items-center gap-6">
                  <div><div className="text-[11px] text-[#8a8a8a]">Current</div><div className="text-[22px] font-bold text-[#2b2b2b]">{money(rent.currentRent)}</div></div>
                  <div><div className="text-[11px] text-[#8a8a8a]">Market estimate</div><div className="text-[22px] font-bold text-[#b8935f]">{money(rent.marketRent)}</div></div>
                  <div><div className="text-[11px] text-[#8a8a8a]">Gap</div><div className={`text-[22px] font-bold ${rent.gapPercent > 0 ? "text-green-600" : "text-[#2b2b2b]"}`}>{rent.gapPercent > 0 ? "+" : ""}{rent.gapPercent}%</div></div>
                </div>
                <div className="bg-[#faf7f2] rounded-lg px-3 py-2 text-[13px] text-[#4a4a4a]"><span className="text-[#b8935f] font-medium">{rent.classification}</span></div>
                <p className="text-[13px] text-[#6a6a6a]">{rent.message}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "withdrawn" && (
        <div className="space-y-3">
          {withdrawn.length === 0 ? <div className="text-[13px] text-[#8a8a8a]">No withdrawn listings detected.</div> :
            withdrawn.map((w) => (
              <div key={w.id} className="bg-white border border-[#f0ece5] rounded-xl p-4 flex items-start gap-4" data-testid={`withdrawn-${w.id}`}>
                <div className="text-center"><div className="text-[20px] font-bold text-[#b8935f]">{w.confidence}%</div><div className="text-[10px] text-[#8a8a8a]">confidence</div></div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#2b2b2b]">{w.owner} <span className="text-[12px] text-[#8a8a8a]">· {w.address}</span></div>
                  <div className="text-[13px] text-[#4a4a4a] mt-0.5 flex items-center gap-1.5"><AlertCircle size={14} className="text-[#b8935f]" /> {w.classification} · removed after {w.daysOnMarket} days</div>
                  <div className="text-[12px] text-[#8a8a8a] mt-1">Est. value {money(w.estimatedValueRange[0])}–{money(w.estimatedValueRange[1])} · <span className="text-[#b8935f]">{w.suggestedApproach}</span></div>
                </div>
              </div>
            ))}
        </div>
      )}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogTitle className="font-display text-[22px] font-300 text-[#2b2b2b] mb-1">Save valuation</DialogTitle>
          <p className="text-[13px] text-[#8a8a8a] mb-4">{val ? `${money(val.estimate)} · ${form.postcode}` : ""}{valAddr ? ` · ${valAddr}` : ""}</p>
          <div className="space-y-3">
            <div>
              <label className="text-[12px] font-500 text-[#4a4a4a] mb-1.5 block">Link to a property (optional)</label>
              <Select value={sf.propertyId} onValueChange={(v) => setSf({ ...sf, propertyId: v })}>
                <SelectTrigger className="h-[44px] border-[#e2d8c8]" data-testid="val-save-property"><SelectValue placeholder="No property link" /></SelectTrigger>
                <SelectContent className="max-h-[240px]">{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.address || p.title}{p.postcode ? ` · ${p.postcode}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <input className={inp} placeholder="Contact name (optional — creates a lead)" value={sf.name} onChange={(e) => setSf({ ...sf, name: e.target.value })} data-testid="val-save-name" />
            <input className={inp} placeholder="Contact email (optional)" value={sf.email} onChange={(e) => setSf({ ...sf, email: e.target.value })} data-testid="val-save-email" />
            <button onClick={saveVal} disabled={saving} data-testid="val-save-confirm" className="w-full h-[46px] rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] disabled:opacity-60 inline-flex items-center justify-center gap-2">{saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminValuationEngine;
